import type {
  ApplicationDocumentTypeEnum,
  CurrencyDictionaryResponse_CryptoCurrencyEnum,
  CurrencyDictionaryResponse_Int32,
  DictionaryEnumResponse_ApplicationDocumentTypeEnum,
  DictionaryEnumResponse_CountryEnum,
  DictionaryEnumResponse_OrganizationDocumentTypeEnum,
  DictionaryEnumResponse_ProcessTypeEnum,
  OrganizationDocumentTypeEnum,
} from '@acme/api';
import { DictionariesService } from '@acme/api/client';
import { countryStore, currencyStore, processTypesStore } from '@acme/domains';
import { createEffect, createEvent, createStore, sample } from 'effector';

export type SelectOption = {
  label: string;
  value: string;
};

export const dictionariesRequested = createEvent();

export const loadDictionariesFx = createEffect(async () => {
  const [
    docTypes,
    currencies,
    cryptoCurrencies,
    countries,
    applicationDocumentTypes,
    organizationDocumentTypes,
  ] = await Promise.all([
    processTypesStore.getProcessTypes(),
    currencyStore.getCurrencies(),
    currencyStore.getCryptoCurrencies(),
    countryStore.getCountries(),
    DictionariesService.getApiV1DictionariesApplicationDocumentTypes(),
    DictionariesService.getApiV1DictionariesOrganizationDocumentTypes(),
  ]);

  const toOption = (
    id: unknown,
    name: string | null | undefined
  ): SelectOption => ({
    value: String(id ?? ''),
    label: name ?? '',
  });

  const toDocumentTypeOption = <T>(item: {
    id?: T;
    name: string | null;
  }): { value: T; label: string } => ({
    value: item.id!,
    label: item.name ?? '',
  });

  return {
    docTypes,
    currencies,
    cryptoCurrencies,
    countries,
    docTypesOptions: (docTypes ?? [])
      .map((i: DictionaryEnumResponse_ProcessTypeEnum) =>
        toOption(i.id, i.name)
      )
      .filter((o) => o.value),
    currencyOptions: (currencies ?? [])
      .map((i: CurrencyDictionaryResponse_Int32) => toOption(i.id, i.name))
      .filter((o) => o.value),
    cryptoCurrencyOptions: (cryptoCurrencies ?? [])
      .map((i: CurrencyDictionaryResponse_CryptoCurrencyEnum) =>
        toOption(i.id, i.name)
      )
      .filter((o) => o.value),
    countryOptions: (countries ?? [])
      .map((i: DictionaryEnumResponse_CountryEnum) => toOption(i.id, i.name))
      .filter((o) => o.value),
    applicationDocumentTypeOptions: (applicationDocumentTypes?.data ?? [])
      .filter(
        (
          item
        ): item is DictionaryEnumResponse_ApplicationDocumentTypeEnum & {
          id: ApplicationDocumentTypeEnum;
        } => item.id != null
      )
      .map((item) => toDocumentTypeOption(item)),
    organizationDocumentTypeOptions: (organizationDocumentTypes?.data ?? [])
      .filter(
        (
          item
        ): item is DictionaryEnumResponse_OrganizationDocumentTypeEnum & {
          id: OrganizationDocumentTypeEnum;
        } => item.id != null
      )
      .map((item) => toDocumentTypeOption(item)),
  };
});

export const $docTypesOptions = createStore<SelectOption[]>([]).on(
  loadDictionariesFx.doneData,
  (_, data) => data.docTypesOptions
);

export const $currencyOptions = createStore<SelectOption[]>([]).on(
  loadDictionariesFx.doneData,
  (_, data) => {
    return data.currencyOptions;
  }
);

export const $cryptoCurrencyOptions = createStore<SelectOption[]>([]).on(
  loadDictionariesFx.doneData,
  (_, data) => data.cryptoCurrencyOptions
);

export const $countryOptions = createStore<SelectOption[]>([]).on(
  loadDictionariesFx.doneData,
  (_, data) => data.countryOptions
);

export const $applicationDocumentTypeOptions = createStore<
  Array<{ value: ApplicationDocumentTypeEnum; label: string }>
>([]).on(
  loadDictionariesFx.doneData,
  (_, data) => data.applicationDocumentTypeOptions
);

export const $organizationDocumentTypeOptions = createStore<
  Array<{ value: OrganizationDocumentTypeEnum; label: string }>
>([]).on(
  loadDictionariesFx.doneData,
  (_, data) => data.organizationDocumentTypeOptions
);

export const $isLoading = loadDictionariesFx.pending;

export const $error = createStore<string | null>(null).on(
  loadDictionariesFx.failData,
  (_, err) =>
    err instanceof Error ? err.message : 'Ошибка загрузки справочников'
);

sample({
  clock: dictionariesRequested,
  target: loadDictionariesFx,
});
